/**
 * Pure utility functions for periodontal data calculations.
 * PSR (Periodontal Screening and Recording) per Danish DSP standard.
 * DSP staging/grading per 2017 World Workshop on Classification.
 */

// ── Types ─────────────────────────────────────────────────────────────────────

export type PerioSite = 'MB' | 'B' | 'DB' | 'ML' | 'L' | 'DL';

export const PERIO_SITES: PerioSite[] = ['MB', 'B', 'DB', 'ML', 'L', 'DL'];

export interface PerioMeasurement {
  tooth_fdi: string;
  site: PerioSite;
  pocket_depth_mm: number | null;
  recession_mm: number | null;
  cal_mm?: number | null; // computed: pd + rec
  bleeding_on_probing: boolean;
  suppuration: boolean;
  mobility: number | null; // 0-3, per tooth (replicated across sites)
  furcation: number | null; // 0-3, per tooth
}

export type PSRScore = 0 | 1 | 2 | 3 | 4 | null;

export interface PSRSextantResult {
  sextantKey: SextantKey;
  teeth: string[];
  score: PSRScore;
}

export type SextantKey = 's1' | 's2' | 's3' | 's4' | 's5' | 's6';

export const SEXTANTS: Record<SextantKey, string[]> = {
  s1: ['17', '16', '15', '14'],
  s2: ['13', '12', '11', '21', '22', '23'],
  s3: ['24', '25', '26', '27'],
  s4: ['47', '46', '45', '44'],
  s5: ['43', '42', '41', '31', '32', '33'],
  s6: ['34', '35', '36', '37'],
};

export type PerioStage = 'I' | 'II' | 'III' | 'IV';
export type PerioGrade = 'A' | 'B' | 'C';

export interface PerioDiagnosis {
  stage: PerioStage | null;
  grade: PerioGrade | null;
  gradeIsDefault: boolean; // true = not enough exams to compute grade definitively
  summary: string;
}

export interface PerioExamSummary {
  examId: string;
  recordedAt: string; // ISO date string
  measurements: PerioMeasurement[];
}

// ── Multi-rooted teeth (furcation relevant) ───────────────────────────────────

export const MULTI_ROOTED_FDI = new Set([
  '16', '17', '26', '27', '36', '37', '46', '47',
  '14', '15', '24', '25', // upper premolars can have 2 roots
]);

export function isMultiRooted(fdi: string): boolean {
  return MULTI_ROOTED_FDI.has(fdi);
}

// ── PSR computation ───────────────────────────────────────────────────────────

/**
 * Computes PSR score 0-4 for each sextant based on measurements.
 *
 * Scoring rules:
 *   4: max PD ≥ 6 mm OR max CAL ≥ 7 mm OR furcation ≥ 2 OR mobility ≥ 2
 *   3: max PD 4-5 mm
 *   1: max PD ≤ 3, BoP present
 *   0: max PD ≤ 3, no BoP
 *   null: no measurements for this sextant
 *
 * Note: score 2 (calculus/defective restoration) requires a future field — skipped.
 */
export function computePSRSextants(
  measurements: PerioMeasurement[],
): Record<SextantKey, PSRScore> {
  const result: Record<SextantKey, PSRScore> = {
    s1: null, s2: null, s3: null, s4: null, s5: null, s6: null,
  };

  for (const key of Object.keys(SEXTANTS) as SextantKey[]) {
    const sextantTeeth = new Set(SEXTANTS[key]);
    const rows = measurements.filter(m => sextantTeeth.has(m.tooth_fdi));

    if (rows.length === 0) {
      result[key] = null;
      continue;
    }

    let maxPD = 0;
    let maxCAL = 0;
    let hasBoP = false;
    let maxFurcation = 0;
    let maxMobility = 0;

    for (const m of rows) {
      if (m.pocket_depth_mm != null) maxPD = Math.max(maxPD, m.pocket_depth_mm);
      const cal = (m.pocket_depth_mm ?? 0) + (m.recession_mm ?? 0);
      maxCAL = Math.max(maxCAL, cal);
      if (m.bleeding_on_probing) hasBoP = true;
      if (m.furcation != null) maxFurcation = Math.max(maxFurcation, m.furcation);
      if (m.mobility != null) maxMobility = Math.max(maxMobility, m.mobility);
    }

    if (maxPD >= 6 || maxCAL >= 7 || maxFurcation >= 2 || maxMobility >= 2) {
      result[key] = 4;
    } else if (maxPD >= 4) {
      result[key] = 3;
    } else if (hasBoP) {
      result[key] = 1;
    } else {
      result[key] = 0;
    }
  }

  return result;
}

// ── DSP staging + grading ─────────────────────────────────────────────────────

/**
 * Computes worst CAL across all measurements in an exam.
 */
function worstCAL(measurements: PerioMeasurement[]): number {
  let worst = 0;
  for (const m of measurements) {
    const cal = (m.pocket_depth_mm ?? 0) + (m.recession_mm ?? 0);
    worst = Math.max(worst, cal);
  }
  return worst;
}

/**
 * Computes perio stage from worst CAL:
 *   I:   1-2 mm
 *   II:  3-4 mm
 *   III: ≥5 mm
 *   IV:  ≥5 mm + ≥1 tooth with mobility ≥ 2
 */
function computeStage(measurements: PerioMeasurement[]): PerioStage | null {
  const cal = worstCAL(measurements);
  if (cal < 1) return null;

  const hasSevereComplication = measurements.some(
    m => (m.mobility ?? 0) >= 2,
  );

  if (cal >= 5) return hasSevereComplication ? 'IV' : 'III';
  if (cal >= 3) return 'II';
  return 'I';
}

/**
 * Computes perio grade from CAL progression rate across exams.
 * Requires ≥ 2 exams that are ≥ 1 year apart for a definitive grade;
 * otherwise returns Grade B as default with gradeIsDefault = true.
 *
 *   A: < 0.25 mm/year
 *   B: 0.25-1.0 mm/year
 *   C: ≥ 1.0 mm/year
 */
function computeGrade(
  exams: PerioExamSummary[],
): { grade: PerioGrade; gradeIsDefault: boolean } {
  if (exams.length < 2) {
    return { grade: 'B', gradeIsDefault: true };
  }

  // Sort oldest → newest
  const sorted = [...exams].sort(
    (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime(),
  );

  const oldest = sorted[0];
  const newest = sorted[sorted.length - 1];

  const yearsApart =
    (new Date(newest.recordedAt).getTime() - new Date(oldest.recordedAt).getTime()) /
    (365.25 * 24 * 3600 * 1000);

  if (yearsApart < 1) {
    return { grade: 'B', gradeIsDefault: true };
  }

  const calOldest = worstCAL(oldest.measurements);
  const calNewest = worstCAL(newest.measurements);
  const progression = (calNewest - calOldest) / yearsApart;

  if (progression < 0.25) return { grade: 'A', gradeIsDefault: false };
  if (progression < 1.0) return { grade: 'B', gradeIsDefault: false };
  return { grade: 'C', gradeIsDefault: false };
}

const STAGE_SUMMARIES: Record<PerioStage, string> = {
  I:   'Tidlig parodontitis',
  II:  'Moderat parodontitis',
  III: 'Svær parodontitis',
  IV:  'Avanceret parodontitis med komplikationer',
};

const GRADE_LABELS: Record<PerioGrade, string> = {
  A: 'langsom progressionshastighed',
  B: 'moderat progressionshastighed',
  C: 'hurtig progressionshastighed',
};

/**
 * Computes DSP diagnosis (stage + grade) from a list of historical exams.
 * The latest exam's measurements drive the stage; grade uses progression over time.
 */
export function computePerioDiagnosis(exams: PerioExamSummary[]): PerioDiagnosis {
  if (exams.length === 0) {
    return { stage: null, grade: null, gradeIsDefault: false, summary: '' };
  }

  const sorted = [...exams].sort(
    (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
  );
  const latest = sorted[0];

  const stage = computeStage(latest.measurements);
  const { grade, gradeIsDefault } = computeGrade(exams);

  if (stage === null) {
    return {
      stage: null,
      grade: null,
      gradeIsDefault: false,
      summary: 'Ingen parodontale problemer registreret.',
    };
  }

  const summary = `Stadie ${stage} · Grad ${grade} — ${STAGE_SUMMARIES[stage]} med ${GRADE_LABELS[grade]}${gradeIsDefault ? ' (estimeret, utilstrækkelige data)' : ''}.`;

  return { stage, grade, gradeIsDefault, summary };
}
